import { Badge, Icon, Text } from '@artprov/design-system'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/common/zustand/store/auth.store'
import { useDdjjPendiente } from '@/common/utils/hooks/useDdjjPendiente'
import { resolveAdminUsuariosUrl } from '@/common/utils/navigation/admin-usuarios-url.js'
import { isInternalMenuUrl } from '@/common/utils/navigation/menu-url.js'
import { resolveMiPerfilUrl } from '@/common/utils/navigation/mi-perfil-url.js'
import { resolveSeleccionCuenta } from '@/common/utils/navigation/seleccion-cuenta.js'
import { usePerfilUsuario } from './hooks/usePerfilUsuario'
import { LABELS } from './constants/user-profile-content.js'
import { getIniciales } from './util/iniciales.js'

const formatCuit = (cuit) => {
  if (cuit?.length !== 11) return cuit || ''
  return `${cuit.slice(0, 2)}-${cuit.slice(2, 10)}-${cuit.slice(10)}`
}

function UserProfileHeader({ nombre, apellido, rol, isLoading }) {
  const iniciales = getIniciales(nombre, apellido)

  return (
    <div className="rounded-t border border-primary-90 bg-primary-98 px-4 py-4">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-90">
          {isLoading ? (
            <div className="h-6 w-6 animate-pulse rounded-full bg-gray-300" />
          ) : (
            <Text variant="subtitle2" className="font-semibold text-[#000000]">
              {iniciales}
            </Text>
          )}
        </div>

        <div className="flex flex-col">
          {isLoading ? (
            <>
              <div className="mb-1 h-4 w-32 animate-pulse rounded bg-gray-300" />
              <div className="h-3 w-24 animate-pulse rounded bg-gray-300" />
            </>
          ) : (
            <>
              <Text variant="subtitle1" className="font-semibold leading-tight text-primary-10">
                {nombre} {apellido}
              </Text>
              <Text variant="body2" className="mt-0.5 text-tertiary-10">
                {rol}
              </Text>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

UserProfileHeader.propTypes = {
  nombre: PropTypes.string.isRequired,
  apellido: PropTypes.string.isRequired,
  rol: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
}

function InfoRow({ label, value, secondaryValue, isLoading }) {
  if (isLoading) {
    return (
      <div className="mb-4">
        <div className="mb-1 h-3 w-16 animate-pulse rounded bg-gray-300" />
        <div className="h-4 w-48 animate-pulse rounded bg-gray-300" />
        {secondaryValue && <div className="mt-1 h-4 w-40 animate-pulse rounded bg-gray-300" />}
      </div>
    )
  }

  return (
    <div className="mb-4">
      <Text variant="body2" className="font-normal text-tertiary-60">
        {label}
      </Text>
      <Text variant="body2" className="mt-1 font-semibold text-tertiary-10">
        {value}
      </Text>
      {secondaryValue && (
        <Text variant="body2" className="mt-1 font-semibold text-tertiary-10">
          {secondaryValue}
        </Text>
      )}
    </div>
  )
}

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  secondaryValue: PropTypes.string,
  isLoading: PropTypes.bool,
}

function MenuOption({ label, icon, onClick, isLogout = false, showChevron = true, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center justify-between px-3 py-3 transition-colors hover:bg-primary-98"
    >
      <div className="flex items-center gap-2">
        {isLogout && <Icon icon={icon} size="small" className="text-primary-10" />}
        <span className="text-sm font-semibold leading-none tracking-[0.1px] text-primary-10">{label}</span>
      </div>
      {/* PBI 81906: chip «DDJJ pendiente» antes del chevron */}
      <div className="flex items-center gap-2">
        {badge ? (
          <Badge variant="filled" state="warning">
            {badge}
          </Badge>
        ) : null}
        {showChevron && !isLogout && (
          <Icon icon="chevron_right" size="small" className="text-gray-400 group-hover:text-gray-600" />
        )}
      </div>
    </button>
  )
}

MenuOption.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.string,
  onClick: PropTypes.func,
  isLogout: PropTypes.bool,
  showChevron: PropTypes.bool,
  badge: PropTypes.string,
}

export default function UserProfileContent({ variant = 'desktop', showLogout = true, onClose }) {
  const navigate = useNavigate()
  const logout = useAuthStore((state) => state.logout)
  const isMobile = variant === 'mobile'
  // PBI 81906: chip «DDJJ pendiente» en «Editar perfil» mientras la DDJJ no esté subida.
  const ddjjPendiente = useDdjjPendiente()

  const {
    nombre,
    apellido,
    rol,
    empresa,
    cuit,
    numeroContrato,
    ejecutivo,
    emailEjecutivo,
    cantidadContratos,
    esAdmin,
    esAdminARToHyS,
    isLoading,
    error,
  } = usePerfilUsuario()

  // PBI 81715: ADMIN ART/HYS siempre ve "Seleccionar cuenta". Todos van a la
  // pantalla React: buscador para Admin ART/HyS (PBI #80676) y grilla para el
  // resto (PBI #80498).
  const {
    mostrarSeleccionCuenta,
    externa: seleccionCuentaExterna,
    url: urlSeleccionCuenta,
  } = resolveSeleccionCuenta({
    esAdminARToHyS,
    cantidadContratos,
  })

  // El módulo nuevo todavía no está habilitado en todos los ambientes: si el ambiente define
  // VITE_LEGACY_ADMIN_USUARIO_URL, el ítem navega a la pantalla del PHP legacy (navegación
  // externa) en vez de a la ruta interna que monta el microfrontend.
  const urlAdminUsuarios = resolveAdminUsuariosUrl()

  // Mismo mecanismo para "Editar perfil": si el ambiente define VITE_LEGACY_MI_PERFIL_URL, navega
  // al PHP legacy en vez de a la ruta interna que monta el microfrontend.
  const urlMiPerfil = resolveMiPerfilUrl()

  const closeAndNavigate = (path) => {
    onClose?.()
    navigate(path)
  }

  const handleSeleccionarCuenta = () => {
    if (seleccionCuentaExterna) {
      onClose?.()
      window.location.href = urlSeleccionCuenta
      return
    }

    closeAndNavigate(urlSeleccionCuenta)
  }

  const handleAdministracionUsuarios = () => {
    if (isInternalMenuUrl(urlAdminUsuarios)) {
      closeAndNavigate(urlAdminUsuarios)
      return
    }

    onClose?.()
    window.location.href = urlAdminUsuarios
  }

  const handleEditarPerfil = () => {
    if (isInternalMenuUrl(urlMiPerfil)) {
      closeAndNavigate(urlMiPerfil)
      return
    }

    onClose?.()
    window.location.href = urlMiPerfil
  }

  const handleCerrarSesion = () => {
    onClose?.()
    logout()
  }

  return (
    <div className={isMobile ? 'w-full px-3 py-5' : undefined}>
      <div
        className={
          isMobile ? 'w-full rounded border border-gray-200' : 'mx-auto mt-5 w-[396px] rounded border border-gray-200'
        }
      >
        <UserProfileHeader
          nombre={nombre || 'Usuario'}
          apellido={apellido || ''}
          rol={rol || 'Usuario'}
          isLoading={isLoading}
        />

        <div className="px-5 py-4">
          {error ? (
            <div className="py-4 text-center">
              <Text variant="body2" className="text-red-500">
                {error}
              </Text>
            </div>
          ) : (
            <>
              <InfoRow label={LABELS.cuenta} value={empresa || '-'} isLoading={isLoading} />
              <InfoRow label={LABELS.cuit} value={formatCuit(cuit) || '-'} isLoading={isLoading} />
              <InfoRow label={LABELS.contrato} value={numeroContrato || '-'} isLoading={isLoading} />
              <InfoRow
                label={LABELS.ejecutivo}
                value={ejecutivo || '-'}
                secondaryValue={emailEjecutivo || '-'}
                isLoading={isLoading}
              />
            </>
          )}
        </div>
      </div>

      <div className={isMobile ? 'mt-3 w-full px-3' : 'mx-auto mt-4 w-[396px]'}>
        <MenuOption
          label={LABELS.editarPerfil}
          badge={ddjjPendiente ? LABELS.ddjjPendiente : undefined}
          onClick={handleEditarPerfil}
        />
        {mostrarSeleccionCuenta && <MenuOption label={LABELS.seleccionarCuenta} onClick={handleSeleccionarCuenta} />}
        {esAdmin && <MenuOption label={LABELS.administracionUsuarios} onClick={handleAdministracionUsuarios} />}

        {showLogout && (
          <div className="mb-4 mt-2 border-t border-tertiary-80 pt-2">
            <MenuOption label={LABELS.cerrarSesion} icon="logout" onClick={handleCerrarSesion} isLogout />
          </div>
        )}
      </div>
    </div>
  )
}

UserProfileContent.propTypes = {
  variant: PropTypes.oneOf(['desktop', 'mobile']),
  showLogout: PropTypes.bool,
  onClose: PropTypes.func,
}
